import { PRODUCT_NAME } from '../constants/brand';
import React, { useMemo, useState } from 'react';
import {
  Box, Chip, Container, Stack, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import MiniDrawer from './MiniDrawer';
import MentalViewMindVault from '../pages/MedicalScribe/Views/MentalViewMindvault';
import PatientSummaryHeader from '../pages/MedicalScribe/PatientSummaryHeader';
import PatientProfilePanel from '../pages/MedicalScribe/PatientProfilePanel';
import PatientWorkspaceTabs from '../pages/MedicalScribe/PatientWorkspaceTabs';
import ConsultationWorkflowPicker from '../pages/MedicalScribe/ConsultationWorkflowPicker';
import PatientDashboardOverview from '../pages/MedicalScribe/Dashboard/PatientDashboardOverview';
import PatientDocuments from '../pages/MedicalScribe/Views/PatientDocuments';
import TourConsultationPreview from './TourConsultationPreview';
import TourDashboardDetails from './TourDashboardDetails';
import HomePracticeTourPreview from './HomePracticeTourPreview';
import { exampleChat, exampleScan, exampleAssessments, exampleListSummary, exampleScanAverage } from './tourClientData';
import { getRiskStyles, getAssessmentMeta, getSeverityColor } from '../pages/MedicalScribe/Dashboard/dashboardDisplayUtils';

const demoReport = [{ id: 'tour-report', type: 'Document', title: 'Consultation report — example', createdAt: '2026-01-01T10:00:00Z', createdBy: 'Demo clinician', isShared: false }];
const noAction = () => {};

function SharingDemo({ homePractice = false, shared = false }) {
  const reports = homePractice ? [{ ...demoReport[0], title: 'Home practice handout — example', isShared: shared }] : demoReport;
  return <Box>
    <Typography variant="h6" sx={{ mb: 2 }}>Files</Typography>
    <PatientDocuments historyItems={reports} isAdminUser isMindvaultLinked
      handleViewItem={noAction} handleDeleteItem={noAction} handleDownloadReport={noAction}
      handlePatientFileUpload={noAction} setAlert={noAction} setOpenDialog={noAction}
      onPreviewItem={noAction} toggleItemSelection={noAction} clearAllSelections={noAction}
      deleteSelectedHistoryItems={noAction} handleItemShare={noAction} />
  </Box>;
}

const SampleChip = () => <Chip label="Demo only — not saved" size="small" sx={{ bgcolor: '#E6FFFB', color: '#0F766E', fontWeight: 700 }} />;

const demoPatients = [
  { id: 'tour-jordan', firstName: 'Jordan', lastName: 'Wong', age: 34, gender: 'Male', presenting_concerns: 'Sleep routine and work-related stress' },
];

const demoCorporateMap = {
  'tour-jordan': exampleListSummary,
};

function PatientListDemo() {
  const [timeRange, setTimeRange] = useState('week');
  const [selectedCaseOwnerFilter, setSelectedCaseOwnerFilter] = useState('ALL');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const filteredItems = useMemo(() => demoPatients.filter((patient) => `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchText.toLowerCase())), [searchText]);
  return <MentalViewMindVault timeRange={timeRange} setTimeRange={setTimeRange} customFrom="" setCustomFrom={() => {}} customTo="" setCustomTo={() => {}} currentOrg={{ id: 'tour-org' }} userDetails={{ id: 'tour-user', name: 'Demo clinician' }} setAlert={() => {}} setNewPatient={() => {}} clearFields={() => {}} teamMembersData={[]} setCaseOwner={() => {}} searchText={searchText} handleSearchChange={(event) => setSearchText(event.target.value)} filteredItems={filteredItems} page={page} rowsPerPage={20} setPage={setPage} handlePageClick={(pageNumber) => setPage(pageNumber - 1)} corporateMap={demoCorporateMap} therapyStats={{ 'tour-jordan': { lastSessionDate: '2026-01-08T10:00:00Z', completedCount: exampleListSummary.appointmentsCount } }} getLastConsultationComplaint={() => ''} selectedCaseOwnerFilter={selectedCaseOwnerFilter} setSelectedCaseOwnerFilter={setSelectedCaseOwnerFilter} currentUserRole="Admin" isVisualTour />;
}


const patient = {
  id: 'tour-jordan', firstName: 'Jordan', lastName: 'Wong', gender: 'Male', age: 34,
  phoneNumber: '55550142', email: 'jordan@example.com', phone_code: '852', hkid: 'DEMO-ID-002',
  birth_date: '1992-01-01', join_date: '2026-01-01', diagnosis: [],
  presenting_concerns: 'Sleep routine and work-related stress', status: 'Active',
  case_owner: ['demo-clinician'], preExistingConditions: ['Asthma (example)'],
  emergency_contact_person: 'Morgan Wong (example)', emergency_contact: '+852 5555 0199',
  remarks: 'Example only. Prefers video consultations and evening appointments. Follow up on sleep routine and work-related stress.',
};

export default function TourPatientDemo({ screen = 'list' }) {
  const isList = screen === 'list';
  const homePractice = screen.startsWith('home-practice-');
  const recordings = ['recordings-upload', 'recordings-ready'].includes(screen);
  const practiceFiles = ['home-practice-files', 'home-practice-share'].includes(screen);
  const tab = practiceFiles ? 'files' : homePractice ? 'consultations' : screen === 'profile' ? 'profile' : recordings || ['consultations', 'detail', 'recording', 'transcript', 'report', 'generate', 'result'].includes(screen) ? 'consultations' : ['files', 'share'].includes(screen) ? 'files' : 'dashboard';
  const workflowMode = homePractice ? 'write-document' : recordings ? 'transcribe-recording' : ['report', 'generate', 'result'].includes(screen) ? 'write-document' : ['detail', 'recording', 'transcript'].includes(screen) ? 'live-consultation' : null;
  return <Container disableGutters maxWidth={false}>
    <Grid container sx={{ overflow: 'auto', minHeight: '100vh', minWidth: '100vw' }}>
      <Grid size={{ xs: 1, md: 0.5 }}><MiniDrawer /></Grid>
      <Grid size="grow"><Box sx={{ minHeight: '100vh', bgcolor: '#F8F9FA', py: 3, px: { xs: 3, md: '50px' } }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography color="text.secondary">{isList ? 'Clients' : 'Client List / Jordan Wong'}</Typography><SampleChip />
        </Stack>
        <Box inert="">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Connected and sharing — example MindVault data. Shared information depends on the client’s permissions and available records. {PRODUCT_NAME} consultation notes and reports are managed separately.</Typography>
          {isList ? <PatientListDemo /> : <>
            <Box data-tour={homePractice ? 'demo-home-practice-client' : undefined}>
              <PatientSummaryHeader patient={patient} editedPatient={patient} isEditing={false} isMindvaultLinkLoading={false} isMindvaultLinked linkedCaseOwnerNames={['Demo clinician']} getCaseOwnerName={() => 'Demo clinician'} setCurrentTab={noAction} />
            </Box>
            <PatientWorkspaceTabs value={tab} onChange={noAction} />
            <Box sx={{ mt: 2 }}>
              {tab === 'dashboard' && <><PatientDashboardOverview
                latestRiskStyle={getRiskStyles(exampleChat.risk_level)} latestRiskSignals={exampleChat.risk_signals}
                latestSession={exampleChat} latestScan={exampleScan} latestScanAverage={exampleScanAverage}
                vitalSummary={{ stressLevel: exampleListSummary.stressLabel, statusSummary: `Abnormal: ${exampleListSummary.vitalFlags.abnormal.join(', ')}` }}
                assessmentGroups={exampleAssessments} getAssessmentMeta={getAssessmentMeta} getSeverityColor={getSeverityColor}
                assessmentsLoading={false} scansLoading={false} scrollTo={noAction} />
                <TourDashboardDetails />
              </>}
              {tab === 'profile' && <PatientProfilePanel patient={patient} editedPatient={patient} isEditing={false} isMindvaultLinked linkedCaseOwnerNames={['Demo clinician']} getCaseOwnerName={() => 'Demo clinician'} teamMembersData={[]} />}
              {tab === 'consultations' && <ConsultationWorkflowPicker workflowMode={workflowMode} showWorkflowSelector showSidebar isRecognizing={false} resetConsultationUI={noAction} setWorkflowMode={noAction} setShowWorkflowSelector={noAction} setShowSidebar={noAction} />}
              {tab === 'consultations' && workflowMode && (homePractice ? <HomePracticeTourPreview screen={screen} /> : <TourConsultationPreview screen={screen} />)}
              {tab === 'files' && (homePractice
                ? <Box component="fieldset" disabled data-tour="demo-home-practice-files" sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}><SharingDemo homePractice shared={screen === 'home-practice-share'} /></Box>
                : <SharingDemo />)}
            </Box>
          </>}
        </Box>
      </Box></Grid>
    </Grid>
  </Container>;
}
